<template>
  <div id="inventoryReport">
    <inventory-entry-detail ref="inventoryEntryDetail" />

    <div class="inventory-report-legend mb-3 p-3">
      <p class="text-sm font-semibold mb-2">Kolonlar (tüm miktarlar hammadde temel biriminde)</p>
      <ul class="text-sm pl-4 m-0 mb-2">
        <li><strong>Önceki Devir:</strong> Seçilen ayın bir önceki ayındaki sayım kapanışında kalan miktar.</li>
        <li><strong>Eklenen:</strong> Seçilen ay içinde stoka giren (Enter) kayıtlarının temel birime çevrilmiş toplamı.</li>
        <li><strong>Tüketim:</strong> Aynı dönemde stoktan çıkan (Exit) kayıtlarının temel birime çevrilmiş toplamı.</li>
        <li><strong>Kalan (Sayım):</strong> Bu sayımda girilen güncel stok miktarı.</li>
        <li><strong>Değer:</strong> (Önceki Devir + Eklenen) − (Kalan + Tüketim) farkı.</li>
      </ul>
      <p class="text-xs text-muted m-0">
        Geçmiş ayların satırları ilgili sayımda hesaplanan değerleri gösterir; yazılım düzeltmesinden sonra
        <strong>yeni oluşturulan</strong> sayımlarda Eklenen/Tüketim birim dönüşümü doğru hesaplanır. Eski dönemleri
        yeniden düzeltmek için veri / operasyon ekibi ile iletişime geçin.
      </p>
    </div>

    <vs-table
      id="inventoryReportTable"
      ref="table"
      :data="transferEntries"
      :max-items="itemsPerPage"
      v-model="selected"
    >
      <div
        slot="header"
        class="w-full flex flex-wrap items-center flex-grow justify-between"
      >
        <vs-row vs-type="flex" vs-w="12" class="mb-3">
          <global-filter
            ref="filterer"
            :tableOptions="tableOptions"
            :filterOptions="filterOptions"
            :filterChips="filterChips"
            :filterOptionNames="filterOptionNames"
            :isActive="isFilterActive"
            @saveOptions="saveOptions"
            @close="isFilterActive = false"
          />
          <vs-row
            id="panelHeaderRow"
            v-show="!isFilterActive"
            vs-type="flex"
            vs-w="12"
            vs-justify="space-between"
          >
            <vs-col
              id="panel-actions"
              vs-type="flex"
              class="w-auto"
              vs-justify="center"
            >
              <vs-row vs-type="flex" vs-w="12">
                <vs-col vs-type="flex" class="w-auto">
                  <vx-tooltip text="Detaylı Filtreleme" position="top">
                    <vs-button
                      @click="isFilterActive = !isFilterActive"
                      class="panel-action text-warning xlarge-button panel-action-box-shadow"
                      color="transparent"
                      icon-pack="pickicon"
                      icon="icon-pick-filter"
                    >
                    </vs-button>
                  </vx-tooltip>
                </vs-col>
                <vs-col vs-type="flex" class="w-auto">
                  <vx-tooltip text="Excel Export" position="top">
                    <vs-button
                      @click="getExcel"
                      class="panel-action text-dark xlarge-button panel-action-box-shadow"
                      color="transparent"
                      icon-pack="feather"
                      icon="icon-download"
                    >
                    </vs-button>
                  </vx-tooltip>
                </vs-col>
                <vs-col vs-type="flex" class="w-auto">
                  <vx-tooltip text="Listeyi Yenile" position="top">
                    <vs-button
                      @click="getTransferEntriesMonthly"
                      class="panel-action text-warning xlarge-button panel-action-box-shadow"
                      color="transparent"
                      icon-pack="feather"
                      icon="icon-refresh-ccw"
                    >
                    </vs-button>
                  </vx-tooltip>
                </vs-col>
              </vs-row>
            </vs-col>
            <vs-col id="filter-chips" vs-type="flex">
              <vs-row
                vs-type="flex"
                vs-w="12"
                vs-justify="center"
                vs-align="center"
              >
                <vs-button
                  id="clearFilterButton"
                  v-if="filterChips.length > 0"
                  @click="clearFilter"
                  class="mr-2 bg-white text-danger panel-action-box-shadow"
                  icon-pack="feather"
                  icon="icon-slash"
                  radius
                  color="transparent"
                />
                <vs-col
                  vs-type="flex"
                  class="w-auto"
                  v-for="(chip, index) in filterChips"
                  :key="index"
                >
                  <vs-chip
                    color="white"
                    class="panel-action-box-shadow px-1"
                    @click="removeFilter(chip.col, index)"
                    v-show="chip.value != ''"
                    closable
                  >
                    <vs-row vs-tpye="flex" vs-w="12">
                      <vs-col vs-tpye="flex" class="w-auto" vs-align="center">
                        <span class="bold text-xsmaller text-dark">
                          {{ chip.name + " :" }}
                        </span>
                      </vs-col>
                      <vs-col
                        vs-type="flex"
                        class="w-auto ml-1"
                        vs-align="center"
                      >
                        <span
                          class="weight-600 italic text-xsmaller text-primary"
                        >
                          {{ chip.value }}
                        </span>
                      </vs-col>
                    </vs-row>
                  </vs-chip>
                </vs-col>
              </vs-row>
            </vs-col>
            <vs-col
              id="table-page-size"
              vs-type="flex"
              class="w-auto"
              vs-justify="flex-end"
            >
              <vs-dropdown
                vs-trigger-click
                class="bg-white cursor-pointer panel-action-box-shadow"
              >
                <div
                  class="px-4 d-theme-border-grey-light d-theme-dark-bg cursor-pointer flex items-center justify-between text-small"
                >
                  <span class="mr-2 page-size-dropdown-span"
                    >{{ (currentPage + 1) * itemsPerPage - (itemsPerPage - 1) }}
                    -
                    {{
                      totalDataRow - (currentPage + 1) * itemsPerPage > 0
                        ? (currentPage + 1) * itemsPerPage
                        : totalDataRow
                    }}
                    of
                    {{ totalDataRow }}</span
                  >
                  <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
                </div>
                <!-- <vs-button class="btn-drop" type="line" color="primary" icon-pack="feather" icon="icon-chevron-down"></vs-button> -->
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="itemsPerPage = 10">
                    <span>10</span>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="itemsPerPage = 15">
                    <span>15</span>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="itemsPerPage = 20">
                    <span>20</span>
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </vs-col>
          </vs-row>
        </vs-row>
      </div>

      <template slot="thead">
        <vs-th class="w-1/5 px-2">
          <vs-button
            class="m-0 p-0 py-1 my-1 w-full text-smaller"
            icon-pack="feather"
            :icon="
              tableOptions.sortBy == 'stock.RawMaterial.Name'
                ? sortingHeadIcon
                : null
            "
            color="primary"
            type="flat"
            @click="setSortingOptions('stock.RawMaterial.Name')"
            >Stok Kodu/Adı</vs-button
          >
        </vs-th>
        <vs-th class="w-2/12 px-0">
          <vs-button
            title="Seçilen ayın bir önceki ayındaki sayım kapanışında kalan miktar (temel birim)."
            class="m-0 p-0 py-1 my-1 text-smaller w-full"
            icon-pack="feather"
            :icon="
              tableOptions.sortBy == 'preMonthTransfer' ? sortingHeadIcon : null
            "
            color="primary"
            type="flat"
            @click="setSortingOptions('preMonthTransfer')"
            >Önceki Devir</vs-button
          >
        </vs-th>
        <vs-th class="w-2/12 px-1">
          <vs-button
            title="Seçilen ay içinde stoka giren (Enter) miktarların temel birime çevrilmiş toplamı."
            class="m-0 p-0 py-1 my-1 text-smaller w-full"
            icon-pack="feather"
            :icon="
              tableOptions.sortBy == 'currMonthSaved' ? sortingHeadIcon : null
            "
            color="primary"
            type="flat"
            @click="setSortingOptions('currMonthSaved')"
          >
            Eklenen</vs-button
          >
        </vs-th>
        <vs-th class="w-2/12 px-1">
          <vs-button
            title="Aynı dönemde stoktan çıkan (Exit) miktarların temel birime çevrilmiş toplamı."
            class="m-0 p-0 py-1 my-1 text-smaller w-full"
            icon-pack="feather"
            :icon="
              tableOptions.sortBy == 'currMonthConsume' ? sortingHeadIcon : null
            "
            color="primary"
            type="flat"
            @click="setSortingOptions('currMonthConsume')"
            >Tüketim</vs-button
          >
        </vs-th>
        <vs-th class="w-2/12 px-1">
          <vs-button
            title="Bu sayımda girilen güncel stok miktarı (temel birim)."
            class="m-0 p-0 py-1 my-1 text-smaller w-full"
            icon-pack="feather"
            :icon="
              tableOptions.sortBy == 'currMonthLeft' ? sortingHeadIcon : null
            "
            color="primary"
            type="flat"
            @click="setSortingOptions('currMonthLeft')"
            >Kalan (Sayım)</vs-button
          >
        </vs-th>
        <vs-th class="w-2/12 px-1">
          <vs-button
            title="(Önceki Devir + Eklenen) − (Kalan + Tüketim) denkleminin sonucu."
            class="m-0 p-0 py-1 my-1 text-smaller w-full"
            icon-pack="feather"
            :icon="
              tableOptions.sortBy == 'resultValue' ? sortingHeadIcon : null
            "
            color="primary"
            type="flat"
            @click="setSortingOptions('resultValue')"
            >Değer</vs-button
          >
        </vs-th>
      </template>

      <template slot-scope="{ data }">
        <vs-tr
          :data="tr"
          :key="indextr"
          v-for="(tr, indextr) in data"
          class="text-smaller"
          :class="{ 'opacity-75 bg-light': tr.stock && tr.stock.rawMaterial && !tr.stock.rawMaterial.active }"
        >
          <vs-td class="w-1/5">
            <p class="font-medium truncate text-center" :class="{ 'text-muted': tr.stock && tr.stock.rawMaterial && !tr.stock.rawMaterial.active }">
              {{ tr.stock.rawMaterial.name }}
              <span v-if="tr.stock && tr.stock.rawMaterial && !tr.stock.rawMaterial.active" class="text-danger italic text-xsmaller ml-1">(Pasif)</span>
            </p>
          </vs-td>

          <vs-td class="w-2/12">
            <p class="font-semibold text-center text-success">
              {{ tr.preTransfer.toFixed(2) }}
            </p>
          </vs-td>

          <vs-td class="w-2/12">
            <p class="font-semibold text-center text-success">
              {{ tr.currentSaved.toFixed(2) }}
            </p>
          </vs-td>

          <vs-td class="w-2/12">
            <p class="font-semibold text-center text-danger">
              {{ tr.currentConsume.toFixed(2) }}
            </p>
          </vs-td>

          <vs-td class="w-2/12">
            <p class="font-semibold text-center text-warning">
              {{ tr.currentLeft.toFixed(2) }}
            </p>
          </vs-td>

          <vs-td class="w-2/12">
            <p
              v-if="tr.resultValue != 0"
              class="text-center font-semibold"
              :class="{ 'text-primary': tr.resultValue > 0, 'text-danger': tr.resultValue < 0 }"
            >
              {{
                tr.resultValue > 0
                  ? "+ " + tr.resultValue.toFixed(2)
                  : tr.resultValue.toFixed(2)
              }}
            </p>
            <p class="text-center" v-else>
              <feather-icon
                icon="CheckIcon"
                style="color: green; width: 1rem; height: 1rem"
              />
            </p>
          </vs-td>
        </vs-tr>
      </template>
    </vs-table>
    <pagination-default
      :totalLenght="calculatePageLength"
      :setCurrentPage="tableOptions.pageIndex"
      @currentPage="getCurrentPage"
    ></pagination-default>
  </div>
</template>

<script>
import GlobalFilter from "../../GlobalFilter";
import {
  getTransferEntriesMonthly,
  getStockTransferExcel,
} from "@/api/MonthlyStockTransfer.js";
import InventoryEntryDetail from "./InventoryEntryDetail.vue";
import PaginationDefault from "../../PaginationDefault";
export default {
  components: {
    PaginationDefault,
    InventoryEntryDetail,
    GlobalFilter,
  },
  data() {
    return {
      selected: "",
      isFilterActive: false,
      tableOptions: {},
      sortingHeadIcon: "",
      sortingHeadIconBool: 1,
      currentPage: 0,
      itemsPerPage: 10,
      transferEntries: [],
      totalDataRow: 0,
      filterOptions: ["branch", "month", "year", "stock"],
      filterOptionNames: ["Şube", "Ay", "Yıl", "Stok"],
      filterChips: [],
    };
  },
  computed: {
    calculatePageLength() {
      let mod = this.totalDataRow % this.itemsPerPage;
      return mod > 0
        ? (this.totalDataRow - mod) / this.itemsPerPage + 1
        : this.totalDataRow / this.itemsPerPage;
    },
  },
  methods: {
    getExcel() {
      getStockTransferExcel(this.tableOptions)
        .then((res) => {
          if (res && res.status == 200) {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            let now = this.$now.date();
            link.setAttribute(
              "download",
              "Envanter Raporu" +
                now.getDate() +
                " " +
                this.$months()[now.getMonth()].name +
                " " +
                now.getFullYear() +
                ".xlsx"
            );
            document.body.appendChild(link);
            link.click();

            this.$vs.notify({
              color: "success",
              title: "Başarılı",
              text: "Excel Export Başarılı !",
            });
          } else {
            this.$vs.notify({
              color: "danger",
              title: "Hata : " + res.status,
              text: "Excel Export İşlemi Başarısız !",
            });
          }
        })
        .catch(() => {
          this.$vs.notify({
            color: "danger",
            title: "Hata !",
            text: "Sunucu Bağlantı Hatası !",
          });
        });
    },
    saveOptions(filterOptions, filterChips) {
      filterChips = filterChips.filter((x) => x.name != undefined);
      this.isFilterActive = false;
      if (_.isEqual(this.tableOptions, filterOptions)) return;
      this.tableOptions = _.cloneDeep(filterOptions);
      this.filterChips = _.cloneDeep(filterChips);
    },
    clearFilter() {
      this.tableOptions = {
        sortBy: "stock.RawMaterial.Name",
        sortDirection: "asc",
        pageIndex: 0,
        pageSize: this.itemsPerPage,
        branch: this.$currentUser.branch,
        stock: null,
        month: this.$getMonth(),
        year: this.$now.date().getFullYear(),
      };
      this.filterChips.length = 0;
    },
    removeFilter(col, index) {
      if (col === "branch") this.tableOptions[col] = this.$currentUser.branch;
      else this.tableOptions[col] = "";
      this.filterChips.splice(index, 1);
    },
    getCurrentPage(currentx) {
      this.currentPage = currentx - 1;
    },
    getTransferEntriesMonthly() {
      getTransferEntriesMonthly(this.tableOptions)
        .then((res) => {
          if (res && res.status === 200) {
            this.transferEntries = res.data.items;
            this.totalDataRow = res.data.totalCount;
          } else {
            this.$vs.notify({
              color: "danger",
              title: "Hata : " + res.status,
              text: "Transfer Girişleri Yüklenemedi !",
            });
          }
        })
        .catch(() => {
          this.$vs.notify({
            color: "danger",
            title: "Hata !",
            text: "Sunucu Bağlantı Hatası !",
          });
        });
    },
    setSortingOptions(val) {
      if (this.tableOptions.sortBy != val) this.sortingHeadIconBool = 1;
      this.tableOptions.sortBy = val;
      switch (this.sortingHeadIconBool) {
        case 1:
          this.sortingHeadIconBool++;
          this.sortingHeadIcon = "icon-arrow-up";
          break;
        case 2:
          this.sortingHeadIconBool++;
          this.sortingHeadIcon = "icon-arrow-down";
          break;
        case 3:
          this.sortingHeadIconBool = 1;
          this.sortingHeadIcon = "";
          this.tableOptions.sortBy = "";
      }
    },
  },
  created() {
    this.clearFilter();
  },
  watch: {
    tableOptions: {
      deep: true,
      handler(val) {
        this.$nextTick(() => {
          if (!val.month) {
            this.tableOptions.month = this.$getMonth();
          }
          if (!val.year) {
            this.tableOptions.year = this.$now.date().getFullYear();
          }
          this.getTransferEntriesMonthly();
        });
      },
    },
    selected: function (val) {
      if (val != "") {
        this.$refs.inventoryEntryDetail.isActive = true;
        this.$refs.inventoryEntryDetail.stockId = val.stock.id;
        this.selected = "";
      }
    },
    currentPage: function (val) {
      this.tableOptions.pageIndex = val;
    },
    sortingHeadIconBool: function () {
      switch (this.sortingHeadIconBool) {
        case 1:
          this.tableOptions.sortDirection = "";
          break;
        case 2:
          this.tableOptions.sortDirection = "asc";
          break;
        case 3:
          this.tableOptions.sortDirection = "desc";
          break;
      }
      this.currentPage = 0;
    },
    itemsPerPage: function (val) {
      this.currentPage = 0;
      this.tableOptions.pageSize = val;
    },
  },
};
</script>

<style>
.inventoryReportOptions .vs__selected {
  color: rgba(var(--vs-primary), 1) !important;
}

.inventoryReportOptions .vs__dropdown-menu {
  max-height: 25vh !important;
  overflow-x: auto !important;
  overflow-y: auto !important;
  width: fit-content !important;
}

.inventoryReportOptions .vs__dropdown-toggle {
  border: none !important;
  border-bottom: 1px solid #00000035 !important;
  border-radius: 0;
}

.inventoryBranchSelect {
  min-width: 221px;
}

.inventoryMonthSelect {
  min-width: 100px;
}

.inventory-report-legend {
  background: #f4f8ff;
  border: 1px solid #c5d9f0;
  border-radius: 6px;
}

.inventoryYearSelect {
  min-width: 90px;
}
</style>
